<template>
  <div
    :class="[
      currentStep == 0
        ? 'bg-bibleMobile md:bg-bible'
        : currentStep == 1
        ? 'bg-registerMobile md:bg-register'
        : 'bg-booksMobile md:bg-books',
    ]"
    class="banner relative h-60 bg-center bg-no-repeat"
  >
    <div
      v-if="showHeaderBar"
      class="absolute inset-x-0 top-0 z-30 flex items-center px-3 pt-3 max-w-3xl mx-auto"
    >
      <!-- Izquierda: flecha + badge de iglesia en mobile -->
      <div class="flex min-w-0 md:w-1/3 items-center gap-2">
        <button
          v-if="showBackButton"
          type="button"
          class="header-icon-btn flex-shrink-0"
          aria-label="Volver"
          @click="$emit('changeStep', currentStep - 1)"
        >
          <img
            src="icons/chevron-left-solid.svg"
            alt=""
            class="header-icon header-icon-back"
          />
        </button>
        <button
          v-if="showChurchName"
          type="button"
          class="min-w-0 rounded-lg bg-black bg-opacity-50 px-2.5 py-1 md:hidden"
          @click="$emit('changeStep', 1)"
        >
          <span class="block truncate text-xs text-white">
            {{ church.churchName }}
          </span>
        </button>
      </div>

      <!-- Centro: badge de iglesia solo en desktop, absolutamente centrado -->
      <div class="md:w-1/3 flex justify-center items-center">
        <button
          v-if="showChurchName"
          type="button"
          class="hidden rounded-lg bg-black bg-opacity-50 px-2.5 py-1 md:block"
          style="max-width: 11rem"
          @click="$emit('changeStep', 1)"
        >
          <span class="block truncate text-sm text-white">
            {{ church.churchName }}
          </span>
        </button>
      </div>

      <div
        class="relative md:w-1/3 ml-auto flex flex-shrink-0 items-center justify-end"
        ref="profileDropdown"
      >
        <button
          v-if="showLogoutButton"
          type="button"
          class="flex items-center gap-1.5 rounded-full bg-white bg-opacity-20 py-1 pl-1 pr-2.5 text-white transition hover:bg-opacity-30"
          @click="profileOpen = !profileOpen"
        >
          <span
            class="flex h-6 w-6 items-center justify-center rounded-full bg-aces text-xs font-bold text-white"
          >
            {{ userInitials }}
          </span>
          <span class="max-w-[5rem] truncate text-sm font-medium">{{
            userFirstName
          }}</span>
          <ChevronDownIcon class="h-3.5 w-3.5 opacity-80" />
        </button>

        <div
          v-if="profileOpen"
          class="absolute right-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-xl bg-white shadow-2xl"
        >
          <div class="flex items-center justify-between px-4 pb-2 pt-4">
            <h3 class="text-base font-bold text-dark-gray">Mis datos</h3>
            <button
              class="text-gray-400 hover:text-gray-600"
              @click="profileOpen = false"
            >
              <XIcon class="h-5 w-5" />
            </button>
          </div>
          <p class="px-4 pb-3 text-xs text-gray-400">
            Se cargan una vez. No hacen falta para comprar.
          </p>

          <div class="flex flex-col gap-2.5 border-t border-gray-100 px-4 py-3">
            <div class="flex justify-between text-sm">
              <span class="text-gray-500">Nombre</span>
              <span class="text-right font-medium text-dark-gray">{{
                userName
              }}</span>
            </div>
            <div class="flex justify-between text-sm">
              <span class="text-gray-500">{{
                authenticatedUser?.docType || "DNI"
              }}</span>
              <span class="font-medium text-dark-gray">{{
                authenticatedUser?.documentNumber
              }}</span>
            </div>
            <div class="flex items-center justify-between text-sm">
              <span class="text-gray-500">Email</span>
              <span
                :class="emailVerified ? 'text-green-600' : 'text-gray-700'"
                class="flex items-center gap-1 font-medium"
              >
                {{ authenticatedUser?.email }}
                <span v-if="emailVerified">✓</span>
              </span>
            </div>
            <div class="flex items-center justify-between text-sm">
              <span class="text-gray-500">Celular</span>
              <span
                :class="phoneVerified ? 'text-green-600' : 'text-gray-700'"
                class="flex items-center gap-1 font-medium"
              >
                {{ authenticatedUser?.phoneNumber }}
                <span v-if="phoneVerified">✓</span>
              </span>
            </div>
            <div v-if="church?.churchName" class="flex justify-between text-sm">
              <span class="text-gray-500">Iglesia</span>
              <span class="text-right font-medium text-dark-gray">{{
                church.churchName
              }}</span>
            </div>
            <div
              v-if="church?.coordinatorName"
              class="flex justify-between text-sm"
            >
              <span class="text-gray-500">Coordinador</span>
              <span class="text-right font-medium text-dark-gray">{{
                church.coordinatorName
              }}</span>
            </div>
          </div>

          <div class="flex flex-col gap-2 border-t border-gray-100 p-4">
            <button
              type="button"
              class="w-full rounded-lg border border-aces px-4 py-2.5 text-sm font-semibold text-aces transition hover:bg-teal-50"
              @click="onEditProfile"
            >
              Editar mis datos
            </button>
            <button
              type="button"
              class="w-full rounded-lg border border-red-200 px-4 py-2.5 text-sm font-semibold text-red-500 transition hover:bg-red-50"
              @click="onLogout"
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      class="flex h-full flex-col gap-2 bg-black bg-opacity-30 text-center text-2xl font-bold text-white sm:text-3xl"
    >
      <div
        class="mb-12 flex h-full flex-col items-center justify-center gap-2 pt-2 sm:py-2 lg:mb-0"
      >
        <h1>
          {{
            currentStep == 0
              ? "Bienvenido"
              : currentStep == 1 && authenticatedUser?.documentNumber
              ? "Mis datos"
              : currentStep == 1
              ? "Registro"
              : currentStep == 2
              ? "Mis Pedidos"
              : currentStep == 3
              ? "Suscripción"
              : currentStep == 4
              ? "Carrito"
              : "Pago"
          }}
        </h1>
      </div>
    </div>
    <div class="absolute bottom-3 z-20 flex justify-center w-full">
      <div
        class="inline-flex items-center gap-1.5 rounded-full bg-white/95 px-4 py-0.5 text-sm text-white bg-darkBlue border-darkBlue border bg-opacity-40 shadow-md"
      >
        Fecha de cierre: <strong>12 de septiembre</strong>
      </div>
    </div>
    <div
      class="absolute bottom-0 h-4 w-full bg-gradient-to-b from-transparent to-background via-transparent lg:hidden"
    />
  </div>

  <div
    v-if="currentStep >= 3"
    class="relative z-10 mx-auto flex max-w-xs flex-row items-center justify-center gap-0 py-4"
  >
    <template v-for="(step, i) in checkoutSteps" :key="step.id">
      <div
        v-if="i > 0"
        :class="
          currentStep > step.globalStep - 1
            ? 'border-aces'
            : 'border-gray-300 border-dashed'
        "
        class="w-10 border-t"
      />
      <div class="flex flex-col items-center">
        <div
          :class="[
            currentStep >= step.globalStep
              ? 'bg-aces text-white'
              : 'border-2 border-gray-300 bg-white text-gray-400',
          ]"
          class="flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold"
        >
          <CheckIcon v-if="currentStep > step.globalStep" class="h-4 w-4" />
          <span v-else>{{ step.id }}</span>
        </div>
        <span
          :class="
            currentStep >= step.globalStep
              ? 'font-medium text-gray-700'
              : 'text-gray-400'
          "
          class="mt-1 text-xs"
          >{{ step.name }}</span
        >
      </div>
    </template>
  </div>
</template>

<script>
  import { ref, watch, computed } from "vue";
  import axios from "axios";
  import { CheckIcon, ChevronDownIcon, XIcon } from "@heroicons/vue/solid";

  export default {
    components: { CheckIcon, ChevronDownIcon, XIcon },
    props: {
      authenticatedUser: {
        type: Object,
        default: () => {},
      },
      currentStep: {
        type: Number,
        default: 1,
      },
      stepsLocked: {
        type: Boolean,
        default: false,
      },
      paymentDone: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["changeStep", "logout"],
    setup(props) {
      const church = ref({});

      const isAuthenticated = computed(
        () => !!props.authenticatedUser?.documentNumber,
      );

      const showHeaderBar = computed(() => props.currentStep > 0);

      const showBackButton = computed(
        () =>
          (props.currentStep > 1 &&
            props.currentStep !== 2 &&
            !(props.currentStep === 5 && props.paymentDone)) ||
          (!isAuthenticated.value && props.currentStep === 1),
      );

      const showLogoutButton = computed(() => isAuthenticated.value);

      const showChurchName = computed(
        () => isAuthenticated.value && !!church.value.churchName,
      );

      const userInitials = computed(() => {
        const first = props.authenticatedUser?.firstName?.[0] ?? "";
        const last = props.authenticatedUser?.lastName?.[0] ?? "";
        return (first + last).toUpperCase();
      });

      const userFirstName = computed(
        () => props.authenticatedUser?.firstName ?? "",
      );

      const userName = computed(() => {
        const first = props.authenticatedUser?.firstName ?? "";
        const last = props.authenticatedUser?.lastName ?? "";
        return [first, last].filter(Boolean).join(" ");
      });

      const emailVerified = computed(() => {
        const u = props.authenticatedUser;
        return !!(u?.emailVerified ?? u?.EmailVerified);
      });

      const phoneVerified = computed(() => {
        const u = props.authenticatedUser;
        return !!(u?.phoneVerified ?? u?.PhoneVerified);
      });

      watch(
        () => props.authenticatedUser?.churchClientId,
        async (churchClientId) => {
          if (!churchClientId) {
            church.value = {};
            return;
          }
          try {
            const { data } = await axios.get(`Churches/ById/${churchClientId}`);
            church.value = data || {};
          } catch (err) {
            console.log("err", err.response);
            church.value = {};
          }
        },
        { immediate: true },
      );

      return {
        church,
        showHeaderBar,
        showBackButton,
        showLogoutButton,
        showChurchName,
        userInitials,
        userFirstName,
        userName,
        emailVerified,
        phoneVerified,
      };
    },
    data() {
      return {
        profileOpen: false,
        checkoutSteps: [
          { id: 1, name: "Elegir", globalStep: 3 },
          { id: 2, name: "Revisar", globalStep: 4 },
          { id: 3, name: "Pagar", globalStep: 5 },
        ],
      };
    },
    mounted() {
      document.addEventListener("click", this.handleOutsideClick);
    },
    beforeUnmount() {
      document.removeEventListener("click", this.handleOutsideClick);
    },
    methods: {
      handleOutsideClick(event) {
        if (
          this.$refs.profileDropdown &&
          !this.$refs.profileDropdown.contains(event.target)
        ) {
          this.profileOpen = false;
        }
      },
      onEditProfile() {
        this.profileOpen = false;
        this.$emit("changeStep", 1);
      },
      onLogout() {
        this.profileOpen = false;
        this.$emit("logout");
      },
    },
  };
</script>

<style scoped>
  .banner {
    background-size: cover;
  }

  @media (min-width: 768px) {
    .banner {
      background-size: max(100%, 1400px) auto;
    }
  }

  .header-icon-btn {
    display: flex;
    height: 2rem;
    width: 2rem;
    flex-shrink: 0;
    cursor: pointer;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    border: 2px solid rgba(255, 255, 255, 0.45);
    background-color: rgba(0, 0, 0, 0.25);
    color: #fff;
    padding: 0;
  }

  .header-icon {
    display: block;
    flex-shrink: 0;
    object-fit: contain;
  }

  /* Chevron SVG es más “pesado”; logout de Heroicons tiene más margen en el viewBox */
  .header-icon-back {
    width: 0.875rem;
    height: 0.875rem;
  }

  .header-icon-logout {
    width: 1.125rem;
    height: 1.125rem;
  }
</style>
